/**
 * BusinessApplicationForm - Comprehensive Business/PUDO Application
 * Multi-step form with business details, documents, and store setup
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  ArrowRight,
  Store,
  Building,
  FileText,
  Upload,
  Check,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Palette,
  Image,
  AlertTriangle,
  Package,
  Navigation
} from 'lucide-react';

// Steps
const STEPS = [
  { id: 1, title: 'Business Info', icon: Building },
  { id: 2, title: 'Location', icon: MapPin },
  { id: 3, title: 'Documents', icon: FileText },
  { id: 4, title: 'Store Setup', icon: Store },
  { id: 5, title: 'Review', icon: Check },
  { id: 6, title: 'Create Account', icon: Check }
];

// Business types
const BUSINESS_TYPES = [
  { id: 'retail', name: 'Retail Store', icon: '🏪', description: 'Physical retail location' },
  { id: 'restaurant', name: 'Restaurant/Food', icon: '🍽️', description: 'Food service business' },
  { id: 'services', name: 'Services', icon: '🔧', description: 'Service-based business' },
  { id: 'wholesale', name: 'Wholesale', icon: '📦', description: 'Bulk/wholesale supplier' },
  { id: 'manufacturer', name: 'Manufacturer', icon: '🏭', description: 'Product manufacturer' },
  { id: 'other', name: 'Other', icon: '🏢', description: 'Other business type' }
];

// Parishes
const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// Store themes
const THEMES = [
  { id: 'green', name: ' Green', color: '#00A86B' },
  { id: 'gold', name: 'Gold Premium', color: '#FFD300' },
  { id: 'classic', name: 'Classic', color: '#ffffff' },
  { id: 'neon', name: 'Neon Glow', color: '#00FF88' }
];

export default function BusinessApplicationForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isPudoApplication = searchParams.get('type') === 'pudo';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Step 1: Business Info
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [description, setDescription] = useState('');
  const [trn, setTrn] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  
  // Step 2: Location
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [parish, setParish] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [landmark, setLandmark] = useState('');
  const [latitude, setLatitude] = useState(null);  const [longitude, setLongitude] = useState(null);  const [gettingLocation, setGettingLocation] = useState(false);  const [addressSuggestions, setAddressSuggestions] = useState([]);  const [showSuggestions, setShowSuggestions] = useState(false);  const [searchingAddress, setSearchingAddress] = useState(false);
  const [operatingHours, setOperatingHours] = useState({    monday: { open: '09:00', close: '17:00', closed: false },    tuesday: { open: '09:00', close: '17:00', closed: false },    wednesday: { open: '09:00', close: '17:00', closed: false },    thursday: { open: '09:00', close: '17:00', closed: false },    friday: { open: '09:00', close: '17:00', closed: false },    saturday: { open: '09:00', close: '14:00', closed: false },    sunday: { open: '09:00', close: '14:00', closed: true }  });
  
  const TIME_OPTIONS = [
    "06:00", "06:30", "07:00", "07:30", "08:00", "08:30", "09:00", "09:30",
    "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
    "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
    "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00"
  ];
  const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  
  const updateHours = (day, field, value) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };
  
  const formatTimeDisplay = (time) => {
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return hour12 + ":" + minutes + " " + ampm;
  };
  
  const formatHoursForApi = () => {
    const result = {};
    DAYS.forEach(day => {
      const dayData = operatingHours[day];
      if (dayData.closed) {
        result[day] = "Closed";
      } else {
        result[day] = formatTimeDisplay(dayData.open) + " - " + formatTimeDisplay(dayData.close);
      }
    });
    return result;
  };


  // Get current location using browser geolocation
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await response.json();
          if (data.address) {
            setStreet(data.address.road || data.address.house_number + " " + (data.address.road || "") || "");
            setCity(data.address.city || data.address.town || data.address.village || "");
            setParish(data.address.county || data.address.state || "");
          }
        } catch (error) {
          console.error("Reverse geocode error:", error);
        }
        toast.success("Location captured!");
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Failed to get location. Please enable location services.");
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Search address using OpenStreetMap Nominatim (free, no API key)
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ", Jamaica")}&format=json&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setAddressSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch (error) {
      console.error("Address search error:", error);
    }
    setSearchingAddress(false);
  };

  // Select address from suggestions
  const selectAddress = (suggestion) => {
    setStreet(suggestion.address?.road || suggestion.display_name.split(",")[0] || "");
    setCity(suggestion.address?.city || suggestion.address?.town || suggestion.address?.village || "");
    setParish(suggestion.address?.county || suggestion.address?.state || "");
    setLatitude(parseFloat(suggestion.lat).toFixed(6));
    setLongitude(parseFloat(suggestion.lon).toFixed(6));
    setShowSuggestions(false);
    toast.success("Address selected!");
  };

  // Debounce address search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (street.length >= 3) {
        searchAddress(street);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [street]);


  // PUDO specific
  const [isPudo, setIsPudo] = useState(isPudoApplication);
  const [pudoCapacity, setPudoCapacity] = useState('');
  const [hasSecureStorage, setHasSecureStorage] = useState(false);
  const [acceptsReturns, setAcceptsReturns] = useState(false);
  
  // Step 3: Documents
  const [businessRegistration, setBusinessRegistration] = useState(null);
  const [businessRegistrationPreview, setBusinessRegistrationPreview] = useState(null);
  const [trnDocument, setTrnDocument] = useState(null);
  const [trnDocumentPreview, setTrnDocumentPreview] = useState(null);
  const [proofOfAddress, setProofOfAddress] = useState(null);
  const [proofOfAddressPreview, setProofOfAddressPreview] = useState(null);
  const [ownerIdDocument, setOwnerIdDocument] = useState(null);
  const [ownerIdPreview, setOwnerIdPreview] = useState(null);
  const [storePhoto, setStorePhoto] = useState(null);
  const [storePhotoPreview, setStorePhotoPreview] = useState(null);
  
  // Step 4: Store Setup
  const [storeName, setStoreName] = useState('');
  const [storeTheme, setStoreTheme] = useState('green');
  const [storeAbout, setStoreAbout] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  // Step 6: Account Creation
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (user) {
      setOwnerName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please select an image or PDF file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = file.type.startsWith('image/') ? reader.result : null;
      
      switch (type) {
        case 'businessRegistration':
          setBusinessRegistration(file);
          setBusinessRegistrationPreview(preview);
          break;
        case 'trn':
          setTrnDocument(file);
          setTrnDocumentPreview(preview);
          break;
        case 'proofOfAddress':
          setProofOfAddress(file);
          setProofOfAddressPreview(preview);
          break;
        case 'ownerId':
          setOwnerIdDocument(file);
          setOwnerIdPreview(preview);
          break;
        case 'storePhoto':
          setStorePhoto(file);
          setStorePhotoPreview(preview);
          break;
        case 'logo':
          setLogoFile(file);
          setLogoPreview(reader.result);
          break;
        case 'banner':
          setBannerFile(file);
          setBannerPreview(reader.result);
          break;
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post(`/security/verify/upload-document?doc_type=${docType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.file_path;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!businessName.trim()) return 'Business name is required';
        if (!businessType) return 'Please select a business type';
        if (!ownerName.trim()) return 'Owner name is required';
        if (!phone.trim()) return 'Phone number is required';
        if (!email.trim()) return 'Email is required';
        return null;
      case 2:
        if (!street.trim()) return 'Street address is required';
        if (!city.trim()) return 'City is required';
        if (!parish) return 'Parish is required';
        return null;
      case 3:
        if (!isPudoApplication && !ownerIdDocument) return "Owner's ID is required";
        return null;
      case 4:
        if (!isPudoApplication && !storeName.trim()) return 'Store name is required';
        return null;
      default:
        return null;
    }
  };

  const nextStep = () => {
    const error = validateStep(currentStep);
    if (error) {
      toast.error(error);
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 6));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    // Validate password
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    setUploading(true);
    
    try {
      // Upload documents
      const uploadedDocs = {};
      
      if (businessRegistration) {
        uploadedDocs.business_registration = await uploadFile(businessRegistration, "business_registration");
      }
      if (trnDocument) {
        uploadedDocs.trn_document = await uploadFile(trnDocument, "trn_document");
      }
      if (proofOfAddress) {
        uploadedDocs.proof_of_address = await uploadFile(proofOfAddress, "proof_of_address");
      }
      if (ownerIdDocument) {
        uploadedDocs.owner_id = await uploadFile(ownerIdDocument, "owner_id");
      }
      if (storePhoto) {
        uploadedDocs.store_photo = await uploadFile(storePhoto, "store_photo");
      }
      
      setUploading(false);
      
      // Use public API based on application type
      const endpoint = isPudoApplication ? "/applications/pudo/apply/public" : "/applications/vendor/apply/public";
      
      const applicationData = isPudoApplication ? {
        business_name: businessName,
        business_type: "pudo",
        contact_name: ownerName,
        email,
        phone,
        address: `${street}, ${city}`,
        parish,
        city,
        operating_hours: formatHoursForApi(),
        storage_capacity: pudoCapacity,
        has_parking: hasSecureStorage,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        password
      } : {
        business_name: businessName,
        business_type: businessType,
        business_description: description,
        trn: trn || null,
        contact_name: ownerName,
        email,
        phone,
        address: `${street}, ${city}`,
        parish,
        city,
        store_name: storeName || businessName,
        store_theme: storeTheme,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        password
      };
      
      const response = await api.post(endpoint, applicationData);
      
      // Store auth token and user info
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
      
      toast.success(isPudoApplication ? "PUDO Centre application submitted!" : "Business application submitted!");
      navigate(isPudoApplication ? "/pudo/dashboard" : "/business/dashboard");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };
  const progress = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                {isPudo ? (
                  <>
                    <Package className="w-5 h-5 text-[#00A86B]" />
                    PUDO Point Application
                  </>
                ) : (
                  <>
                    <Store className="w-5 h-5 text-[#00A86B]" />
                    Business Application
                  </>
                )}
              </h1>
              <p className="text-sm text-slate-400">
                {isPudo ? 'Register as a pickup/drop-off point' : 'Register your business on '}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <Progress value={progress} className="h-2 bg-slate-800" />
          <div className="flex justify-between mt-4">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted ? 'bg-[#00A86B]' :
                    isActive ? 'bg-[#00A86B]/20 border-2 border-[#00A86B]' :
                    'bg-slate-800'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className={`w-5 h-5 ${isActive ? 'text-[#00A86B]' : 'text-slate-500'}`} />
                    )}
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${isActive ? 'text-white' : 'text-slate-500'}`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Step 1: Business Info */}
        {currentStep === 1 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#00A86B]" />
              Business Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Business Name *</Label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Your business name"
                  className="bg-slate-900 border-slate-700 mt-1"
                  data-testid="business-name-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-300 mb-3 block">Business Type *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {BUSINESS_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setBusinessType(type.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        businessType === type.id
                          ? 'border-[#00A86B] bg-[#00A86B]/10'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`business-type-${type.id}`}
                    >
                      <span className="text-xl">{type.icon}</span>
                      <p className="font-medium text-sm mt-1">{type.name}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Business Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us about your business..."
                  className="bg-slate-900 border-slate-700 mt-1 min-h-[80px]"
                  data-testid="business-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">TRN (Tax Registration)</Label>
                  <Input
                    value={trn}
                    onChange={(e) => setTrn(e.target.value)}
                    placeholder="123-456-789"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="business-trn-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Owner Name *</Label>
                  <Input
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Owner's full name"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="owner-name-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Phone Number *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (876) 000-0000"
                      className="bg-slate-900 border-slate-700 pl-10"
                      data-testid="business-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="business@email.com"
                      className="bg-slate-900 border-slate-700 pl-10"
                      data-testid="business-email-input"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Website (Optional)</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://yourbusiness.com"
                    className="bg-slate-900 border-slate-700 pl-10"
                    data-testid="business-website-input"
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Location */}
        {currentStep === 2 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#00A86B]" />
              Business Location
            </h2>
            
            <div className="space-y-4">
              {/* Use Current Location Button */}
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00A86B]/20 text-[#00A86B] rounded-lg hover:bg-[#00A86B]/30 transition-all text-sm font-medium border border-[#00A86B]/30"
                  data-testid="use-location-button"
                >
                  {gettingLocation ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#00A86B] border-t-transparent rounded-full animate-spin" />
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4" />
                      Use Current Location
                    </>
                  )}
                </button>
              </div>
              
              {/* Address Autocomplete */}
              <div className="relative">
                <Label className="text-slate-300">Street Address *</Label>
                <div className="relative mt-1">
                  <Input
                    value={street}
                    onChange={(e) => {
                      setStreet(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Start typing address for suggestions..."
                    className="bg-slate-900 border-slate-700"
                    data-testid="business-street-input"
                  />
                  {searchingAddress && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Address Suggestions Dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        onClick={() => selectAddress(suggestion)}
                        className="px-4 py-3 hover:bg-slate-700 cursor-pointer border-b border-slate-700 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-[#00A86B] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-white font-medium">{suggestion.display_name.split(",").slice(0, 2).join(",")}</p>
                            <p className="text-xs text-slate-400">{suggestion.display_name.split(",").slice(2, 5).join(",")}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Coordinates Display */}
              {(latitude || longitude) && (
                <div className="flex gap-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex-1">
                    <Label className="text-slate-400 text-xs">Latitude</Label>
                    <p className="text-white font-mono text-sm">{latitude}</p>
                  </div>
                  <div className="flex-1">
                    <Label className="text-slate-400 text-xs">Longitude</Label>
                    <p className="text-white font-mono text-sm">{longitude}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">City *</Label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="business-city-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Parish *</Label>
                  <select
                    value={parish}
                    onChange={(e) => setParish(e.target.value)}
                    className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                    data-testid="business-parish-select"
                  >
                    <option value="">Select parish...</option>
                    {PARISHES.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Postal Code</Label>
                  <Input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Postal code"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="business-postal-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Landmark</Label>
                  <Input
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                    placeholder="Near landmark"
                    className="bg-slate-900 border-slate-700 mt-1"
                    data-testid="business-landmark-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300 mb-3 block flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00A86B]" />
                  Operating Hours
                </Label>
                <div className="space-y-2 bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                  {DAYS.map((day) => (
                    <div key={day} className="flex items-center gap-3">
                      <div className="w-24">
                        <span className="text-sm text-slate-300 capitalize">{day.slice(0, 3)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <button
                          type="button"
                          onClick={() => updateHours(day, "closed", !operatingHours[day].closed)}
                          className={`w-16 h-8 rounded text-xs font-medium transition-all \${
                            operatingHours[day].closed 
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                              : 'bg-green-500/20 text-green-400 border border-green-500/30'
                          }`}
                        >
                          {operatingHours[day].closed ? 'Closed' : 'Open'}
                        </button>
                        {!operatingHours[day].closed && (
                          <>
                            <select
                              value={operatingHours[day].open}
                              onChange={(e) => updateHours(day, "open", e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                            >
                              {TIME_OPTIONS.map(t => (
                                <option key={t} value={t}>{formatTimeDisplay(t)}</option>
                              ))}
                            </select>
                            <span className="text-slate-500">to</span>
                            <select
                              value={operatingHours[day].close}
                              onChange={(e) => updateHours(day, "close", e.target.value)}
                              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                            >
                              {TIME_OPTIONS.map(t => (
                                <option key={t} value={t}>{formatTimeDisplay(t)}</option>
                              ))}
                            </select>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* PUDO Options */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="p-4 bg-[#00A86B]/10 rounded-xl border border-[#00A86B]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Package className="w-6 h-6 text-[#00A86B]" />
                      <div>
                        <p className="font-medium">Enable as PUDO Point</p>
                        <p className="text-sm text-slate-400">Accept package pickups and drop-offs</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsPudo(!isPudo)}
                      className={`w-14 h-8 rounded-full transition-all ${
                        isPudo ? 'bg-[#00A86B]' : 'bg-slate-700'
                      }`}
                      data-testid="pudo-toggle"
                    >
                      <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                        isPudo ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {isPudo && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <Label className="text-slate-300">Package Capacity</Label>
                        <select
                          value={pudoCapacity}
                          onChange={(e) => setPudoCapacity(e.target.value)}
                          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white"
                        >
                          <option value="">Select capacity...</option>
                          <option value="small">Small (1-10 packages)</option>
                          <option value="medium">Medium (10-50 packages)</option>
                          <option value="large">Large (50+ packages)</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="secure-storage"
                          checked={hasSecureStorage}
                          onCheckedChange={setHasSecureStorage}
                          className="border-[#00A86B]"
                        />
                        <Label htmlFor="secure-storage" className="text-slate-300 cursor-pointer">
                          Has secure storage area
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="accepts-returns"
                          checked={acceptsReturns}
                          onCheckedChange={setAcceptsReturns}
                          className="border-[#00A86B]"
                        />
                        <Label htmlFor="accepts-returns" className="text-slate-300 cursor-pointer">
                          Accepts return packages
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Documents */}
        {currentStep === 3 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00A86B]" />
              Required Documents
            </h2>
            
            <div className="space-y-6">
              {/* Owner ID */}
              <div>
                <Label className="text-slate-300 mb-2 block">Owner's ID (TRN/Passport) *</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {ownerIdPreview ? (
                      <img src={ownerIdPreview} alt="ID" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileSelect(e, 'ownerId')}
                      className="hidden"
                      id="owner-id-upload"
                    />
                    <label htmlFor="owner-id-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload ID
                        </span>
                      </Button>
                    </label>
                    {ownerIdDocument && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {ownerIdDocument.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Business Registration */}
              <div>
                <Label className="text-slate-300 mb-2 block">Business Registration Certificate</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {businessRegistrationPreview ? (
                      <img src={businessRegistrationPreview} alt="Registration" className="w-full h-full object-cover" />
                    ) : (
                      <Building className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileSelect(e, 'businessRegistration')}
                      className="hidden"
                      id="business-reg-upload"
                    />
                    <label htmlFor="business-reg-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Certificate
                        </span>
                      </Button>
                    </label>
                    {businessRegistration && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {businessRegistration.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* TRN Document */}
              <div>
                <Label className="text-slate-300 mb-2 block">TRN Document</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {trnDocumentPreview ? (
                      <img src={trnDocumentPreview} alt="TRN" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileSelect(e, 'trn')}
                      className="hidden"
                      id="trn-upload"
                    />
                    <label htmlFor="trn-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload TRN
                        </span>
                      </Button>
                    </label>
                    {trnDocument && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {trnDocument.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Store Photo */}
              <div>
                <Label className="text-slate-300 mb-2 block">Store/Location Photo</Label>
                <div className="flex items-start gap-4">
                  <div className="w-32 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {storePhotoPreview ? (
                      <img src={storePhotoPreview} alt="Store" className="w-full h-full object-cover" />
                    ) : (
                      <Store className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, 'storePhoto')}
                      className="hidden"
                      id="store-photo-upload"
                    />
                    <label htmlFor="store-photo-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </span>
                      </Button>
                    </label>
                    {storePhoto && (
                      <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {storePhoto.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div className="text-sm text-yellow-400">
                    <p className="font-medium">Document Guidelines:</p>
                    <ul className="mt-1 space-y-1 text-yellow-400/80">
                      <li>• All documents must be clear and readable</li>
                      <li>• Information must match your application</li>
                      <li>• Expired documents will be rejected</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: Store Setup */}
        {currentStep === 4 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#00A86B]" />
              Store Setup
            </h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300">Store Display Name *</Label>
                <Input
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="How customers will see your store"
                  className="bg-slate-900 border-slate-700 mt-1"
                  data-testid="store-display-name-input"
                />
              </div>
              
              {/* Logo Upload */}
              <div>
                <Label className="text-slate-300 mb-2 block">Store Logo</Label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e, 'logo')}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label htmlFor="logo-upload">
                      <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-500 mt-2">400x400px recommended</p>
                  </div>
                </div>
              </div>
              
              {/* Banner Upload */}
              <div>
                <Label className="text-slate-300 mb-2 block">Store Banner</Label>
                <div className="w-full h-32 rounded-xl bg-slate-900 border-2 border-dashed border-slate-700 flex items-center justify-center overflow-hidden mb-3">
                  {bannerPreview ? (
                    <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-slate-600">
                      <Image className="w-10 h-10 mx-auto mb-1" />
                      <p className="text-sm">No banner</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, 'banner')}
                  className="hidden"
                  id="banner-upload"
                />
                <label htmlFor="banner-upload">
                  <Button variant="outline" className="border-slate-700 cursor-pointer" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Banner
                    </span>
                  </Button>
                </label>
              </div>
              
              {/* Theme Selection */}
              <div>
                <Label className="text-slate-300 mb-3 block">Store Theme</Label>
                <div className="grid grid-cols-4 gap-3">
                  {THEMES.map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setStoreTheme(theme.id)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        storeTheme === theme.id
                          ? 'border-[#00A86B] bg-[#00A86B]/10'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div 
                        className="w-8 h-8 rounded-full mx-auto mb-2 border-2 border-slate-600"
                        style={{ backgroundColor: theme.color }}
                      />
                      <p className="text-xs text-center">{theme.name}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">About Your Store</Label>
                <Textarea
                  value={storeAbout}
                  onChange={(e) => setStoreAbout(e.target.value)}
                  placeholder="Tell customers about your business..."
                  className="bg-slate-900 border-slate-700 mt-1 min-h-[100px]"
                  data-testid="store-about-input"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Check className="w-5 h-5 text-[#00A86B]" />
              Review Application
            </h2>
            
            <div className="space-y-4">
              {/* Business Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4 text-[#00A86B]" />
                  Business Information
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-slate-400">Name:</p>
                  <p>{businessName}</p>
                  <p className="text-slate-400">Type:</p>
                  <p>{BUSINESS_TYPES.find(t => t.id === businessType)?.name || businessType}</p>
                  <p className="text-slate-400">Owner:</p>
                  <p>{ownerName}</p>
                  <p className="text-slate-400">Phone:</p>
                  <p>{phone}</p>
                  {trn && (
                    <>
                      <p className="text-slate-400">TRN:</p>
                      <p>{trn}</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Location Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00A86B]" />
                  Location
                </h3>
                <p className="text-sm">{street}</p>
                <p className="text-sm">{city}, {parish}</p>
                {isPudo && (
                  <p className="text-sm text-[#00A86B] mt-2 flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    PUDO Point Enabled
                  </p>
                )}
              </div>
              
              {/* Store Summary */}
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Store className="w-4 h-4 text-[#00A86B]" />
                  Store Settings
                </h3>
                <div className="flex items-center gap-4">
                  {logoPreview && (
                    <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
                  )}
                  <div>
                    <p className="font-medium">{storeName}</p>
                    <p className="text-sm text-slate-400">
                      Theme: {THEMES.find(t => t.id === storeTheme)?.name}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl">
                <p className="text-sm text-[#00A86B]">
                  By submitting this application, you confirm that all information provided is accurate and agree to 's merchant terms and conditions.
                </p>
              </div>
            </div>
          </Card>
        )}


        {/* Step 6: Account Creation */}
        {currentStep === 6 && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Check className="w-5 h-5 text-[#00A86B]" />
              Create Your Account
            </h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl">
                <p className="text-sm text-[#00A86B]">
                  {isPudoApplication 
                    ? "Create a password to access your PUDO Centre portal and track your application status."
                    : "Create a password to access your business portal and track your application status."}
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    disabled
                    className="bg-slate-900/50 border-slate-700 text-slate-400"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Create Password *</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="bg-slate-900 border-slate-700"
                    data-testid="password-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Confirm Password *</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="bg-slate-900 border-slate-700"
                    data-testid="confirm-password-input"
                  />
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <h3 className="font-medium mb-2 text-slate-300">What happens next?</h3>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Your application will be reviewed within 2-3 business days</li>
                  <li>• You can login to check your application status anytime</li>
                  <li>• We will notify you via email when approved</li>
                  {!isPudoApplication && <li>• Once approved, you can start listing products on your store</li>}
                </ul>
              </div>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex gap-4 mt-6">
          {currentStep > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              className="border-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          
          {currentStep < 6 ? (
            <Button
              type="button"
              onClick={nextStep}
              className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
              data-testid="submit-business-application-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploading ? 'Uploading documents...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
